import React from 'react';
import { Link } from 'react-router-dom';
import { FiArrowRight, FiCheckCircle, FiShield, FiUsers, FiBriefcase, FiFileText, FiLock, FiGlobe } from 'react-icons/fi';
import './CounterpartyProfile.css';

const CounterpartyProfile = () => {
  const requirements = [
    {
      icon: <FiUsers />,
      title: 'Institutional Investors',
      items: [
        'Sovereign wealth funds',
        'Family offices',
        'Pension funds',
        'Asset managers',
        'Private equity firms'
      ]
    },
    {
      icon: <FiBriefcase />,
      title: 'Corporate Principals',
      items: [
        'Energy & resource companies',
        'Real estate developers',
        'Commodity traders',
        'Infrastructure operators',
        'Industrial groups'
      ]
    },
    {
      icon: <FiShield />,
      title: 'Compliance Standards',
      items: [
        'Strict KYC protocols',
        'AML compliance',
        'Regulatory standing verification',
        'Transparent corporate structure',
        'Banking relationship validation'
      ]
    }
  ];

  const onboardingSteps = [
    {
      number: '01',
      title: 'Initial Contact',
      description: 'Confidential discussion under NDA to assess mutual alignment'
    },
    {
      number: '02',
      title: 'Documentation',
      description: 'Submission of credentials, proof of funds, and corporate structure'
    },
    {
      number: '03',
      title: 'Verification',
      description: 'KYC/AML checks and professional reference validation'
    },
    {
      number: '04',
      title: 'Qualification',
      description: 'Formal counterparty approval and engagement authorization'
    }
  ];

  return (
    <div className="counterparty-profile">
      {/* Hero Section */}
      <section className="counterparty-hero">
        <div className="container">
          <h1 className="counterparty-hero-title">Counterparty Profile</h1>
          <p className="counterparty-hero-subtitle">
            We partner selectively with institutional investors, family offices, and corporate 
            principals who demonstrate clear mandates, financial capacity, and regulatory compliance.
          </p>
        </div>
      </section>

      {/* Target Partnerships */}
      <section className="target-section">
        <div className="container">
          <h2 className="section-title center">Target Partnerships</h2>
          <p className="section-subtitle center">
            Defining the ideal entities for strategic collaboration
          </p>
          <div className="target-grid">
            {requirements.map((req, index) => (
              <div key={index} className="target-card scale-in">
                <div className="target-icon">{req.icon}</div>
                <h3 className="target-title">{req.title}</h3>
                <ul className="target-list">
                  {req.items.map((item, i) => (
                    <li key={i}>
                      <FiCheckCircle className="target-list-icon" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance & Governance */}
      <section className="compliance-section">
        <div className="container">
          <div className="compliance-grid">
            <div className="compliance-content fade-in">
              <h2 className="section-title">Compliance & Governance</h2>
              <p className="compliance-text">
                We maintain strict KYC (Know Your Counterparty) and AML protocols to ensure 
                the highest standards of regulatory compliance and operational integrity.
              </p>
              <div className="compliance-features">
                <div className="compliance-feature">
                  <FiShield className="feature-icon" />
                  <div>
                    <h4>KYC Protocols</h4>
                    <p>Rigorous verification of identity, corporate structure, and beneficial ownership</p>
                  </div>
                </div>
                <div className="compliance-feature">
                  <FiLock className="feature-icon" />
                  <div>
                    <h4>AML Compliance</h4>
                    <p>Anti-money laundering checks aligned with international standards</p>
                  </div>
                </div>
                <div className="compliance-feature">
                  <FiGlobe className="feature-icon" />
                  <div>
                    <h4>Regulatory Standing</h4>
                    <p>Verification of licenses, permits, and regulatory history across jurisdictions</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="compliance-visual slide-in-right">
              <div className="visual-badge">
                <FiShield className="badge-icon" />
                <span>Institutional Grade Compliance</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Onboarding Standards */}
      <section className="onboarding-section">
        <div className="container">
          <h2 className="section-title center">Onboarding Standards</h2>
          <p className="section-subtitle center">
            Requirements for transparency and operational readiness
          </p>
          <div className="onboarding-steps">
            {onboardingSteps.map((step, index) => (
              <div key={index} className="onboarding-step">
                <div className="step-number">{step.number}</div>
                <div className="step-content">
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </div>
                {index < onboardingSteps.length - 1 && <div className="step-arrow">
                  <FiArrowRight />
                </div>}
              </div>
            ))}
          </div>
          <div className="onboarding-requirements">
            <h3>Required Documentation</h3>
            <div className="requirements-grid">
              <div className="requirement-item">
                <FiFileText />
                <span>Certificate of Incorporation</span>
              </div>
              <div className="requirement-item">
                <FiFileText />
                <span>Proof of Funds / Financial Capacity</span>
              </div>
              <div className="requirement-item">
                <FiFileText />
                <span>Corporate Structure Chart</span>
              </div>
              <div className="requirement-item">
                <FiFileText />
                <span>Regulatory Licenses</span>
              </div>
              <div className="requirement-item">
                <FiFileText />
                <span>Banking References</span>
              </div>
              <div className="requirement-item">
                <FiFileText />
                <span>Professional References</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="counterparty-cta">
        <div className="container">
          <div className="cta-content">
            <h2>Qualified Counterparties</h2>
            <p>
              To explore potential alignment, qualified parties may submit a brief capability 
              statement and proof of capacity under existing NDA.
            </p>
            <Link to="/contact" className="btn-primary">
              Submit Capability Statement
              <FiArrowRight />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CounterpartyProfile;